/* eslint-disable @typescript-eslint/no-unsafe-argument */
import { Box, SystemProps } from "flicket-ui";
import {
  EventQuery,
  VenueAddressFragment,
  VenueInfoFragment,
} from "~graphql/sdk";
import {
  DesktopSubHeader,
  SubHeader,
} from "~components/common/common.BaseSubHeader";
import { useOrganization } from "~hooks";
import { getEventVenueLocation } from "~lib/helpers/getEventVenueLocation";
import { formatEventDateRange } from "~lib/helpers/formatDate";
import { pick } from "@styled-system/props";

type DesktopDetailProps = EventQuery["event"] & {
  showSeatsCTA: boolean;
  isSinglePage: boolean;
  setIsOpen: (isOpen: boolean) => void;
};

interface EventTitleProps
  extends
    Omit<SystemProps, "title">,
    Pick<
      DesktopDetailProps,
      "isSinglePage" | "title" | "startDate" | "endDate"
    > {
  hideBackLink?: boolean;
  backToLinkText?: string;
  backToLink?: string;
  tourId?: string;
  colour?: SystemProps["color"];
  variant?: "legacy" | "new";
  venue: VenueInfoFragment & VenueAddressFragment;
}

export interface UseEventTitleProps {
  startDate: string;
  endDate?: string | null;
  venue: VenueInfoFragment & VenueAddressFragment;
}

export const useEventTitle = ({
  startDate,
  venue,
  endDate,
}: UseEventTitleProps) => {
  const { hasFeature } = useOrganization();

  const venueName = getEventVenueLocation(venue);

  return {
    eventDateString: formatEventDateRange(startDate, endDate, {
      format: "basicLong",
      showStartTime: !hasFeature("HideEventTime"),
      timeZone: venue?.timezone,
      locale: venue?.locale,
    }),
    venueName,
  };
};

export const EventTitle = ({
  isSinglePage,
  backToLinkText = "Back to Events",
  backToLink = "/",
  hideBackLink = false,
  title,
  startDate,
  endDate,
  venue,
  tourId,
  colour = "white",
  variant = "legacy",
  ...props
}: EventTitleProps) => {
  const { eventDateString, venueName } = useEventTitle({
    startDate,
    venue,
    endDate,
  });

  const subTitle = (
    <>
      {variant === "new" && venueName
        ? `${eventDateString}, `
        : eventDateString}
      &nbsp;
      {!!venueName && (
        <Box
          as="span"
          d={{
            _: variant === "new" ? "inline-block" : "none",
            md: "inline-block",
          }}
        >
          {variant === "new" ? "" : "- "}
          {venueName}
        </Box>
      )}
    </>
  );

  if (variant === "new") {
    return (
      <SubHeader
        title={title}
        subTitle={subTitle}
        isSinglePage={isSinglePage}
        backToLinkText={backToLinkText}
        backToLink={tourId ? `/tours/${tourId}` : backToLink}
        hideBackLink={hideBackLink}
        textColor={colour}
        {...pick(props)}
      />
    );
  }

  return (
    <DesktopSubHeader
      title={title}
      subTitle={subTitle}
      isSinglePage={isSinglePage}
      backToLinkText={backToLinkText}
      backToLink={tourId ? `/tours/${tourId}` : backToLink}
      hideBackLink={hideBackLink}
      colour={colour}
    />
  );
};
